import useSWR from 'swr';

import { fetchEndpointData } from '@/utils/fetch.client';

/**
 * What a space is entitled to spend, for the period containing now.
 *
 * The whole rule is one subtraction, and the backend does it:
 *
 * ```text
 * available = allowance + credit_balance - period_spend
 * ```
 *
 * Two things combine into it, and they behave differently on purpose. The
 * **licence allowance** resets every period and does not roll over. **Credits**
 * persist until spent, and are only drawn on once the allowance is used up.
 *
 * Money is integer **micro-USD**, the unit the ledgers store — format at the edge
 * with `formatUsd` from `@/api-models/usage`, never round in transit.
 */
export interface TeamBalance {
  /** `free` | `starter` | `growth` | `scale`. */
  license_tier: LicenseTier;
  /** Billed seats: the staff override, else the active member count. */
  seats: number;
  seats_are_overridden: boolean;
  /** RFC 3339. The period anchor; `null` falls back to the space's creation date. */
  license_started_at: string | null;
  /** RFC 3339 bounds of the open period, `[start, end)`. */
  period_start: string;
  period_end: string;
  allowance_micro_usd: number;
  period_spent_micro_usd: number;
  /**
   * May be **negative**: spend is only known once a request completes, so requests
   * in flight land past zero. The debt is carried and repaid out of the next
   * period's allowance rather than becoming a permanent lockout.
   */
  credit_balance_micro_usd: number;
  /** `allowance + credits - spent`. Zero or below means work stops. */
  available_micro_usd: number;
  is_exhausted: boolean;
  /** The margin the allowance was derived at, so the figure can be explained. */
  margin_percent: number;
}

export type LicenseTier = 'free' | 'starter' | 'growth' | 'scale';

export const LICENSE_TIERS: { value: LicenseTier; label: string; pricePerSeat: number }[] = [
  { value: 'free', label: 'Free', pricePerSeat: 0 },
  { value: 'starter', label: 'Starter', pricePerSeat: 20 },
  { value: 'growth', label: 'Growth', pricePerSeat: 80 },
  { value: 'scale', label: 'Scale', pricePerSeat: 200 },
];

export function tierLabel(tier: string): string {
  return LICENSE_TIERS.find((t) => t.value === tier)?.label ?? tier;
}

/**
 * A space's own balance, readable by any member.
 *
 * Not polled: it moves as requests complete, and this is read for review. The
 * enforcement path reads it server-side on every proxied request.
 */
export function useTeamBalance(teamId: string | null) {
  const key = teamId ? `/api/v1/teams/${teamId}/balance` : null;
  const { data, error, isLoading, mutate } = useSWR(key, () => fetchEndpointData<TeamBalance>(key!), {
    revalidateOnFocus: false,
    keepPreviousData: true,
  });
  return { balance: data ?? null, error, isLoading, mutate };
}

/** Fraction of the entitlement still unspent, clamped to 0–1 for meters. */
export function balanceFraction(balance: TeamBalance): number {
  const total = balance.allowance_micro_usd + Math.max(balance.credit_balance_micro_usd, 0);
  if (total <= 0) return 0;
  return Math.min(Math.max(balance.available_micro_usd / total, 0), 1);
}
